import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, RotateCw, Loader2, Users } from "lucide-react";

const fmt = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export const EmailedCopies = () => {
  const [copies, setCopies] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/email-copies").then(({ data }) => setCopies(data)).catch(() => {}); }, []);

  const resend = async (c) => {
    setBusy(c.token);
    try {
      const { data } = await api.post(`/email-copies/${c.token}/resend`, { include_recipient: true });
      setCopies((list) => list.map((x) => (x.token === c.token
        ? { ...x, last_sent_at: data.last_sent_at, expires_at: data.expires_at, send_count: (x.send_count || 1) + 1 } : x)));
      toast.success(`Sent again to ${data.email}${data.recipient_email ? ` and ${data.recipient_email}` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not resend");
    } finally {
      setBusy("");
    }
  };

  if (!copies.length) return null;
  return (
    <div className="mt-16" data-testid="emailed-copies-section">
      <p className="overline text-accent mb-2 flex items-center gap-2"><Mail size={14} /> Emailed copies</p>
      <div className="grid-card divide-y divide-border">
        {copies.map((c) => (
          <div key={c.token} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`emailed-copy-${c.token}`}>
            <div className="min-w-0">
              <p className="font-medium text-foreground truncate"><span className="font-mono text-xs mr-2">{c.form_number}</span>{c.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Last sent {fmt(c.last_sent_at || c.created_at)}{c.send_count > 1 ? ` · ${c.send_count} times` : ""}
                {c.recipient_email && <span className="inline-flex items-center gap-1 ml-2"><Users size={12} /> also to {c.recipient_email}</span>}
              </p>
            </div>
            <div className="flex gap-2">
              {new Date(c.expires_at) > new Date() && (
                <Link to={`/copies/${c.token}`} data-testid={`emailed-copy-open-${c.token}`}>
                  <Button variant="ghost" size="sm" className="rounded-sm">Download</Button>
                </Link>
              )}
              <Button size="sm" variant="outline" onClick={() => resend(c)} disabled={busy === c.token}
                data-testid={`emailed-copy-resend-${c.token}`} className="rounded-sm gap-2 hover:bg-secondary">
                {busy === c.token ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Resend
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
